import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { api, auth } from '../lib/api';
import { format } from 'date-fns';
import SafeIcon from '../common/SafeIcon';
import Notification from '../components/Admin/Notification';
import * as FiIcons from 'react-icons/fi';

const { 
  FiUser, FiCreditCard, FiCalendar, FiShield, FiArrowUpRight, 
  FiCheckCircle, FiTrash2, FiLock, FiAlertTriangle, FiZap, FiInfo 
} = FiIcons;

const Account = () => {
  const { profile, user, refreshProfile, signOut } = useAuth();
  const navigate = useNavigate();
  
  const [fullName, setFullName] = useState(profile?.full_name || '');
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [isUpdating, setIsUpdating] = useState(false);
  const [notify, setNotify] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');

  const isPro = profile?.subscription_status === 'pro';
  const remaining = Math.max(0, (profile?.free_searches_limit || 10) - (profile?.free_searches_used || 0));

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setIsUpdating(true);
    try {
      await api('updateProfile', { full_name: fullName });
      await refreshProfile();
      setNotify({ message: 'Profile updated successfully', type: 'success' });
    } catch (err) {
      setNotify({ message: err.message, type: 'error' });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (passwords.new !== passwords.confirm) {
      return setNotify({ message: 'New passwords do not match', type: 'error' });
    }
    setIsUpdating(true);
    try {
      await auth('change-password', {
        currentPassword: passwords.current,
        newPassword: passwords.new,
        revokeOtherSessions: false
      });
      setPasswords({ current: '', new: '', confirm: '' });
      setNotify({ message: 'Password updated successfully', type: 'success' });
    } catch (err) {
      setNotify({ message: err.message, type: 'error' });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') return;
    setIsUpdating(true);
    try {
      // Removes the app profile, then signs out. The sign-in identity itself is not deleted.
      await api('deleteAccount');
      await signOut();
      navigate('/login');
    } catch (err) {
      setNotify({ message: err.message, type: 'error' });
      setIsUpdating(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8 text-left pb-20">
      {notify && <Notification {...notify} onClose={() => setNotify(null)} />}

      <div>
        <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tight">Account Settings</h1>
        <p className="text-slate-500 dark:text-slate-400 font-medium">Manage your profile, security, and subscription.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2 space-y-8">
          
          {/* Profile Section */}
          <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-8 shadow-sm">
            <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2">
              <SafeIcon icon={FiUser} className="text-blue-500" /> Basic Information
            </h3>
            <form onSubmit={handleUpdateProfile} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Full Name</label>
                  <input 
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-3 text-sm font-bold text-slate-900 dark:text-white outline-none focus:border-blue-500 transition-all"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Enter your name"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Email Address</label>
                  <input 
                    className="w-full bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-3 text-sm font-bold text-slate-400 dark:text-slate-500 cursor-not-allowed"
                    value={profile?.email || user?.email || ''}
                    disabled
                  />
                </div>
              </div>
              <button 
                type="submit" 
                disabled={isUpdating}
                className="px-8 py-3 bg-blue-600 hover:bg-blue-500 text-white font-black rounded-xl shadow-lg shadow-blue-900/20 transition-all text-sm disabled:opacity-50"
              >
                {isUpdating ? 'SAVING...' : 'Save Profile'}
              </button>
            </form>
          </section>

          {/* Password Section */}
          <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-8 shadow-sm">
            <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2">
              <SafeIcon icon={FiLock} className="text-blue-500" /> Security
            </h3>
            <form onSubmit={handleUpdatePassword} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-1.5 sm:col-span-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Current Password</label>
                  <input 
                    type="password"
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-3 text-sm font-bold text-slate-900 dark:text-white outline-none focus:border-blue-500 transition-all"
                    value={passwords.current}
                    onChange={(e) => setPasswords({...passwords, current: e.target.value})}
                    autoComplete="current-password"
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">New Password</label>
                  <input 
                    type="password"
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-3 text-sm font-bold text-slate-900 dark:text-white outline-none focus:border-blue-500 transition-all"
                    value={passwords.new}
                    onChange={(e) => setPasswords({...passwords, new: e.target.value})}
                    placeholder="Min 8 characters"
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Confirm New Password</label>
                  <input 
                    type="password"
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-3 text-sm font-bold text-slate-900 dark:text-white outline-none focus:border-blue-500 transition-all"
                    value={passwords.confirm}
                    onChange={(e) => setPasswords({...passwords, confirm: e.target.value})}
                    placeholder="Repeat new password"
                  />
                </div>
              </div>
              <button 
                type="submit" 
                disabled={isUpdating || !passwords.new || !passwords.current}
                className="px-8 py-3 bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-black rounded-xl transition-all text-sm flex items-center gap-2 disabled:opacity-50"
              >
                Change Password
              </button>
            </form>
          </section>

          {/* Danger Zone */}
          <section className="bg-red-50/50 dark:bg-red-500/5 border border-red-100 dark:border-red-500/20 rounded-3xl p-8">
            <div className="flex items-start gap-4">
              <div className="bg-red-500 p-3 rounded-2xl text-white">
                <SafeIcon icon={FiTrash2} className="w-6 h-6" />
              </div>
              <div className="flex-1 space-y-4">
                <div>
                  <h3 className="text-xl font-black text-red-600 dark:text-red-400">Danger Zone</h3>
                  <p className="text-sm text-red-700/60 dark:text-red-400/60 font-medium">Once you delete your account, there is no going back. All search history and flagging data will be purged.</p>
                </div>
                <button 
                  onClick={() => setShowDeleteModal(true)}
                  className="px-6 py-3 bg-red-600 hover:bg-red-500 text-white font-black rounded-xl shadow-lg shadow-red-900/20 transition-all text-sm"
                >
                  Delete Account
                </button>
              </div>
            </div>
          </section>
        </div>

        {/* Sidebar: Subscription Info */}
        <div className="space-y-6">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-sm overflow-hidden relative">
            {isPro && <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/10 rounded-full -mr-12 -mt-12" />}
            
            <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2">
              <SafeIcon icon={FiCreditCard} className="text-blue-500" /> Subscription
            </h4>
            
            <div className="space-y-6">
              <div className="space-y-1">
                <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Current Plan</div>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 rounded-lg text-xs font-black uppercase tracking-wider ${isPro ? 'bg-emerald-500/10 text-emerald-500' : 'bg-slate-100 dark:bg-slate-800 text-slate-500'}`}>
                    {profile?.subscription_status || 'free'}
                  </span>
                  {isPro && <SafeIcon icon={FiCheckCircle} className="text-emerald-500" />}
                </div>
              </div>

              {!isPro ? (
                <div className="space-y-4">
                  <div className="p-4 bg-amber-50 dark:bg-amber-500/5 rounded-2xl border border-amber-100 dark:border-amber-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <SafeIcon icon={FiZap} className="text-amber-500 w-4 h-4" />
                      <span className="text-[10px] font-black text-amber-600 uppercase tracking-widest">Free Usage</span>
                    </div>
                    <div className="text-2xl font-black text-slate-900 dark:text-white">{remaining} <span className="text-xs text-slate-500">Searches Left</span></div>
                    <div className="w-full bg-slate-200 dark:bg-slate-800 h-1.5 rounded-full overflow-hidden mt-3">
                      <div className="h-full bg-amber-500" style={{ width: `${(remaining / 10) * 100}%` }} />
                    </div>
                  </div>
                  <button 
                    onClick={() => navigate('/pricing')}
                    className="w-full py-4 bg-blue-600 hover:bg-blue-500 text-white font-black rounded-2xl shadow-lg shadow-blue-900/20 transition-all flex items-center justify-center gap-2"
                  >
                    Upgrade to Pro <SafeIcon icon={FiArrowUpRight} />
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-slate-100 dark:border-slate-800">
                    <span className="text-xs font-bold text-slate-500">Next Renewal</span>
                    <span className="text-xs font-bold text-slate-900 dark:text-white">
                      {profile?.subscription_end ? format(new Date(profile.subscription_end), 'MMM dd, yyyy') : 'N/A'}
                    </span>
                  </div>
                  <button className="w-full py-3 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white font-bold rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-100 transition-all flex items-center justify-center gap-2">
                    Manage Billing <SafeIcon icon={FiArrowUpRight} />
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="bg-blue-600 rounded-3xl p-6 text-white shadow-xl shadow-blue-900/20">
            <SafeIcon icon={FiShield} className="w-8 h-8 mb-4 border-2 border-white/20 p-1.5 rounded-lg" />
            <h4 className="text-lg font-bold mb-2">Data Protection</h4>
            <p className="text-xs text-blue-100 font-medium leading-relaxed">
              Your account security is our priority. We use industry-standard encryption to protect your search history and technician data.
            </p>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-md">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl w-full max-w-md shadow-2xl p-8 text-center animate-in zoom-in duration-300">
            <div className="w-20 h-20 bg-red-50 dark:bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiAlertTriangle} className="w-10 h-10 text-red-500" />
            </div>
            <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-3">Are you absolutely sure?</h2>
            <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed mb-8">
              This action cannot be undone. To confirm, please type <span className="font-black text-red-600 select-none">DELETE</span> below.
            </p>
            <input 
              className="w-full bg-slate-50 dark:bg-slate-950 border-2 border-slate-200 dark:border-slate-800 rounded-2xl px-4 py-4 text-center text-lg font-black text-red-600 outline-none focus:border-red-500 transition-all mb-6"
              placeholder="Type DELETE"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
            />
            <div className="flex gap-3">
              <button 
                onClick={() => { setShowDeleteModal(false); setDeleteConfirm(''); }}
                className="flex-1 py-4 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 font-bold rounded-2xl hover:bg-slate-200 transition-all"
              >
                Cancel
              </button>
              <button 
                onClick={handleDeleteAccount}
                disabled={deleteConfirm !== 'DELETE' || isUpdating}
                className="flex-1 py-4 bg-red-600 hover:bg-red-500 disabled:bg-slate-300 dark:disabled:bg-slate-800 text-white font-black rounded-2xl transition-all shadow-lg shadow-red-900/20"
              >
                {isUpdating ? 'DELETING...' : 'Confirm Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Account;